/**
 * @flow
 */
import SyncAlt from '@mui/icons-material/SyncAlt';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from 'mdi-react/ExpandMoreIcon';
import React from 'react';
import { I18n } from 'utils/i18n';
import OidcInteraction from 'services/api/OidcInteraction';

const AccordionSummaryBold = styled(AccordionSummary)({
  '&.Mui-expanded p': {
    fontWeight: 'bold',
  },
});

type Props = {
  client: Object,
  scopes: Array<string>,
  interactionUid: string,
  clientType: string,
};

const logoSx = {
  width: 60,
  height: 60,
  bgcolor: theme => theme.palette.white,
};

const OAuthAuthorize = ({ client, scopes, interactionUid, clientType }: Props) => {
  const isSso = clientType === 'sso';

  const submitTo = url => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    if (document.body) {
      document.body.appendChild(form);
    }
    form.submit();
  };

  const allow = () => submitTo(OidcInteraction.confirmUrl(interactionUid));
  const cancel = () => submitTo(OidcInteraction.abortUrl(interactionUid));

  const scopeGroupsMap = {
    attachment: 'message',
    company: 'company',
    employee: 'company',
    group: 'company',
    message: 'message',
    openid: 'company',
    profile: 'company',
    email: 'company',
  };

  const scopeGroups = scopes.reduce(
    (acc, scope) => {
      const [entity, level] = scope.includes(':') ? scope.split(':') : [scope, 'read'];
      const group = scopeGroupsMap[entity] || 'company';
      const bucket = level === 'write' ? 'write' : 'read';
      const ret = { ...acc };
      ret[bucket][group] = [...(ret[bucket][group] || []), scope];
      return ret;
    },
    { read: {}, write: {} },
  );

  const ScopesAccordion = ({ level }: { level: string }) => {
    if (Object.keys(scopeGroups[level]).length === 0) {
      return null;
    }
    return (
      <Grid size={12}>
        <Typography variant="h3">
          {I18n.t(`OAuth.scopeLevels.${level}`, { name: client.clientName })}
        </Typography>
        <Box sx={{ mt: 2, mb: 3 }}>
          {Object.keys(scopeGroups[level]).map(group => (
            <Accordion key={group}>
              <AccordionSummaryBold expandIcon={<ExpandMoreIcon />}>
                <Typography>{I18n.t(`OAuth.scopeGroup.${group}.${level}`)}</Typography>
              </AccordionSummaryBold>
              {scopeGroups[level][group].map(scope => (
                <AccordionDetails key={scope}>
                  <Typography>
                    {I18n.t(`OAuth.scope.${scope.split(':').join('.')}`, { defaultValue: scope })}
                  </Typography>
                </AccordionDetails>
              ))}
            </Accordion>
          ))}
        </Box>
      </Grid>
    );
  };

  const titleKey = isSso ? 'OAuth.Sign in to app' : 'OAuth.App requests permission';

  return (
    <div>
      <Grid
        spacing={2}
        container
        sx={{
          width: 'initial',
          display: 'flex',
          alignItems: 'center',
          color: 'secondary.main',
          mb: 3,
          justifyContent: 'center',
          flex: '1 0 auto',
        }}
      >
        <Grid size="auto">
          {client.logoUri ? (
            <Box component="img" alt={client.clientName} src={client.logoUri} sx={logoSx} />
          ) : (
            <Box sx={logoSx} />
          )}
        </Grid>
        <Grid size="auto">
          <SyncAlt />
        </Grid>
        <Grid size="auto">
          <Box
            component="img"
            alt="Ommnio"
            src="https://web.ommnio.com/ommnio-favicon.svg"
            sx={logoSx}
          />
        </Grid>
      </Grid>
      <Grid size="auto">
        <Typography variant="h2" align="center" gutterBottom>
          {I18n.t(titleKey, { name: client.clientName })}
        </Typography>
      </Grid>
      {!isSso && <ScopesAccordion level="read" />}
      {!isSso && <ScopesAccordion level="write" />}
      <Grid
        spacing={2}
        container
        sx={{
          width: 'initial',
          display: 'flex',
          alignItems: 'center',
          color: 'secondary.main',
          mb: 3,
          justifyContent: 'center',
          flex: '1 0 auto',
        }}
      >
        <Grid size="auto">
          <Button
            onClick={cancel}
            variant="outlined"
            color="secondary"
            sx={{ color: 'primary.ultraDark' }}
          >
            {I18n.t('OAuth.Cancel')}
          </Button>
        </Grid>
        <Grid size="auto">
          <Button onClick={allow} variant="contained" color="primary" autoFocus>
            {I18n.t(isSso ? 'OAuth.Sign in' : 'OAuth.Allow')}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default OAuthAuthorize;
